import { isResultSectionWidth, type ResultSectionWidth } from '../result-layout.ts';
import type { FlowComposerPreviewSample } from './flow-composer.ts';
import type { AnyViewEnvelope, FlowViewType } from './views.ts';
import { RECIPE_VIEW_TYPES } from './views.ts';

/**
 * Canonical model of a composite tool result.
 *
 * Replaces the legacy "1 tool = 1 view" assumption: a tool result is a set of
 * perspectives (tabs), each composed of ordered canonical sections, each section
 * backed by one canonical view. The renderer (`ToolResultPageRenderer`) already
 * knows how to lay sections out with the anchor nav; this model is what the planner
 * produces and what the runtime emits.
 *
 * Invariant: nothing here is hardcoded business content. Labels and sample values
 * are authored by the LLM (guided by the Output Grammar + skills); the code only
 * arranges them into canonical views.
 */

/** Canonical section roles, aligned with docs/ai-memory/OUTPUT_GRAMMAR.md §2. */
export const RESULT_SECTION_ROLES = [
  'summary',
  'artifact',
  'actions',
  'context',
  'detail',
] as const;
export type ResultSectionRole = typeof RESULT_SECTION_ROLES[number];

/**
 * Visibility scope of a result tab. `run` = data from the current run only.
 * `team` / `tenant` = aggregated across the team's / tenant's runs of this tool
 * (cross-run surfaces such as "Feedback équipe" or "Bibliothèque des calls").
 */
export const RESULT_TAB_SCOPES = ['run', 'team', 'tenant'] as const;
export type ResultTabScope = typeof RESULT_TAB_SCOPES[number];

/** Exact runtime origin. Omitted only by tools saved before source binding. */
export interface ResultSectionSource {
  kind: 'execution_summary' | 'final_output_path';
  /** Dot path in the final primitive output; empty for execution_summary. */
  path: string;
}

export interface ResultSectionSpec {
  /** Stable id, unique within a tab. */
  id: string;
  role: ResultSectionRole;
  viewType: FlowViewType;
  /** Métier label authored by the LLM (e.g. "Cibles prioritaires", "Coaching"). */
  label: string;
  source?: ResultSectionSource;
  /** A missing required section makes the result incomplete, never silently shorter. */
  required?: boolean;
  /** Width in the tab (full by default); side-by-side sections share a row. */
  width?: ResultSectionWidth;
  /** Illustrative, LLM-authored sample for the Forge preview. Runtime fills the real envelope. */
  previewSample?: FlowComposerPreviewSample;
}

export interface ResultTabSpec {
  id: string;
  /** Métier label (e.g. "Analyse", "Coaching", "Feedback équipe", "Bibliothèque"). */
  label: string;
  scope: ResultTabScope;
  /** Authoritative display order, preserved from preview to saved result. */
  sections: ResultSectionSpec[];
}

export interface ResultComposition {
  /** At least one tab; the first is the default perspective. */
  tabs: ResultTabSpec[];
}

export const isResultSectionRole = (value: unknown): value is ResultSectionRole =>
  typeof value === 'string' && RESULT_SECTION_ROLES.includes(value as ResultSectionRole);

export const isResultTabScope = (value: unknown): value is ResultTabScope =>
  typeof value === 'string' && RESULT_TAB_SCOPES.includes(value as ResultTabScope);

const VIEW_TYPE_BY_ROLE: Record<ResultSectionRole, FlowViewType> = {
  summary: 'score_card',
  artifact: 'artifact_document',
  actions: 'action_list',
  context: 'entity_summary',
  detail: 'data_table',
};

export const defaultViewTypeForRole = (role: ResultSectionRole): FlowViewType =>
  VIEW_TYPE_BY_ROLE[role];

/** Maps a section's view type to its canonical section role (renderer grouping). */
const ROLE_BY_VIEW_TYPE: Record<FlowViewType, ResultSectionRole> = {
  artifact_document: 'artifact',
  score_card: 'summary',
  health_gauge: 'summary',
  action_list: 'actions',
  entity_summary: 'context',
  metric_grid: 'detail',
  data_table: 'detail',
  chart: 'detail',
  timeline: 'detail',
};

export const roleForViewType = (viewType: FlowViewType): ResultSectionRole =>
  ROLE_BY_VIEW_TYPE[viewType];

// ---------------------------------------------------------------------------
// Materialized run result (flow_executions.result_payload)
// ---------------------------------------------------------------------------

export interface ResultPayloadSection {
  id: string;
  role: ResultSectionRole;
  viewType: FlowViewType;
  label: string;
  /** Canonical envelope produced by the Result Normalizer for this section. */
  envelope: AnyViewEnvelope;
  /** Width designed for the section (full by default). */
  width?: ResultSectionWidth;
}

export interface ResultPayloadTab {
  id: string;
  label: string;
  scope: ResultTabScope;
  /**
   * Run-scoped tabs carry materialized sections. Team/tenant tabs persist their
   * spec only — their content is aggregated across runs at read time, not at
   * run completion (a single run cannot materialize a cross-run surface).
   */
  sections: ResultPayloadSection[];
  /**
   * The LLM-designed section specs of an aggregate (team/tenant) tab, preserved so
   * read-time aggregation renders what the conversation designed (labels, roles,
   * view types) instead of a generic surface.
   */
  sectionSpecs?: ResultSectionSpec[];
}

export interface ResultPayload {
  tabs: ResultPayloadTab[];
  status?: 'complete' | 'incomplete';
  issues?: string[];
}

// ---------------------------------------------------------------------------
// Dependency-free runtime parsing (usable from Edge _shared and web).
// JSONB round-trips lose static types; these guards re-establish them without
// pulling zod into shared/.
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFlowViewTypeValue = (value: unknown): value is FlowViewType =>
  typeof value === 'string' && RECIPE_VIEW_TYPES.includes(value as FlowViewType);

const parseSectionSpec = (value: unknown): ResultSectionSpec | null => {
  if (!isRecord(value)) return null;
  if (typeof value.id !== 'string' || value.id.length === 0) return null;
  if (!isResultSectionRole(value.role)) return null;
  if (!isFlowViewTypeValue(value.viewType)) return null;
  if (typeof value.label !== 'string' || value.label.length === 0) return null;
  let source: ResultSectionSource | undefined;
  if (value.source !== undefined) {
    if (!isRecord(value.source)) return null;
    if (value.source.kind !== 'execution_summary' && value.source.kind !== 'final_output_path') return null;
    if (typeof value.source.path !== 'string') return null;
    const path = value.source.path.trim();
    if (value.source.kind === 'final_output_path' && !isResultSourcePath(path)) return null;
    if (value.source.kind === 'execution_summary' && path !== '') return null;
    source = { kind: value.source.kind, path };
  }
  if (value.required !== undefined && typeof value.required !== 'boolean') return null;
  // An unknown width is ignored (full row) rather than rejecting the design.
  const width = isResultSectionWidth(value.width) && value.width !== 'full' ? value.width : undefined;
  const previewSample = isRecord(value.previewSample)
    ? value.previewSample as unknown as FlowComposerPreviewSample
    : undefined;
  return {
    id: value.id,
    role: value.role,
    viewType: value.viewType,
    label: value.label,
    ...(source ? { source } : {}),
    ...(typeof value.required === 'boolean' ? { required: value.required } : {}),
    ...(width ? { width } : {}),
    ...(previewSample ? { previewSample } : {}),
  };
};

/** Parses a stored ResultComposition (flows.result_composition). Null when invalid. */
export const parseResultComposition = (value: unknown): ResultComposition | null => {
  if (!isRecord(value) || !Array.isArray(value.tabs) || value.tabs.length === 0) return null;
  const tabs: ResultTabSpec[] = [];
  for (const rawTab of value.tabs) {
    if (!isRecord(rawTab)) return null;
    if (typeof rawTab.id !== 'string' || rawTab.id.length === 0) return null;
    if (typeof rawTab.label !== 'string' || rawTab.label.length === 0) return null;
    if (!isResultTabScope(rawTab.scope)) return null;
    if (!Array.isArray(rawTab.sections) || rawTab.sections.length === 0) return null;
    const sections: ResultSectionSpec[] = [];
    for (const rawSection of rawTab.sections) {
      const section = parseSectionSpec(rawSection);
      if (!section) return null;
      if (sections.some((existing) => existing.id === section.id)) return null;
      sections.push(section);
    }
    if (tabs.some((existing) => existing.id === rawTab.id)) return null;
    tabs.push({ id: rawTab.id, label: rawTab.label, scope: rawTab.scope, sections });
  }
  return { tabs };
};

/** Parses a stored ResultPayload (flow_executions.result_payload). Null when invalid. */
export const parseResultPayload = (value: unknown): ResultPayload | null => {
  if (!isRecord(value) || !Array.isArray(value.tabs) || value.tabs.length === 0) return null;
  const tabs: ResultPayloadTab[] = [];
  for (const rawTab of value.tabs) {
    if (!isRecord(rawTab)) return null;
    if (typeof rawTab.id !== 'string' || rawTab.id.length === 0) return null;
    if (typeof rawTab.label !== 'string' || rawTab.label.length === 0) return null;
    if (!isResultTabScope(rawTab.scope)) return null;
    if (!Array.isArray(rawTab.sections)) return null;
    const sections: ResultPayloadSection[] = [];
    for (const rawSection of rawTab.sections) {
      if (!isRecord(rawSection)) return null;
      if (typeof rawSection.id !== 'string' || rawSection.id.length === 0) return null;
      if (!isResultSectionRole(rawSection.role)) return null;
      if (!isFlowViewTypeValue(rawSection.viewType)) return null;
      if (typeof rawSection.label !== 'string') return null;
      if (!isRecord(rawSection.envelope) || typeof rawSection.envelope.view_type !== 'string') return null;
      sections.push({
        id: rawSection.id,
        role: rawSection.role,
        viewType: rawSection.viewType,
        label: rawSection.label,
        envelope: rawSection.envelope as unknown as AnyViewEnvelope,
        ...(isResultSectionWidth(rawSection.width) && rawSection.width !== 'full' ? { width: rawSection.width } : {}),
      });
    }
    let sectionSpecs: ResultSectionSpec[] | undefined;
    if (Array.isArray(rawTab.sectionSpecs)) {
      const parsed: ResultSectionSpec[] = [];
      for (const rawSpec of rawTab.sectionSpecs) {
        const spec = parseSectionSpec(rawSpec);
        if (spec) parsed.push(spec);
      }
      if (parsed.length > 0) sectionSpecs = parsed;
    }
    tabs.push({
      id: rawTab.id,
      label: rawTab.label,
      scope: rawTab.scope,
      sections,
      ...(sectionSpecs ? { sectionSpecs } : {}),
    });
  }
  return {
    tabs,
    ...(value.status === 'complete' || value.status === 'incomplete' ? { status: value.status } : {}),
    ...(Array.isArray(value.issues) ? { issues: value.issues.filter((issue): issue is string => typeof issue === 'string') } : {}),
  };
};

export const isResultSourcePath = (path: string): boolean =>
  /^[A-Za-z_][\w]*(?:\.(?:[A-Za-z_][\w]*|\d+))*$/.test(path)
  && !path.split('.').some((part) => ['__proto__', 'prototype', 'constructor'].includes(part));

/** Admission for newly designed compositions; stored legacy tools remain readable. */
export const getResultCompositionIssues = (composition: ResultComposition): string[] => {
  if (!parseResultComposition(composition)) return ['result_composition_invalid'];
  const totalSections = composition.tabs.reduce((count, tab) => count + tab.sections.length, 0);
  const seenSources = new Set<string>();
  return composition.tabs.flatMap((tab) => {
    if (tab.scope !== 'run') return [`${tab.id}:scope_requires_admitted_source`];
    return tab.sections.flatMap((section) => {
      if (!section.source) return [`${tab.id}/${section.id}:source_required`];
      if (section.source.kind === 'execution_summary' && totalSections > 1) {
        return [`${tab.id}/${section.id}:composite_requires_exact_source`];
      }
      const identity = `${section.viewType}:${section.source.kind}:${section.source.path}`;
      if (seenSources.has(identity)) return [`${tab.id}/${section.id}:duplicate_source_view`];
      seenSources.add(identity);
      return [];
    });
  });
};
